import {defineStore} from "pinia";
import {computed, ref, watch} from "vue";
import {IUser} from "@/models/IUser";
import {useI18n} from "vue-i18n";
import {useTripsStore} from "./tripsStore";
import * as AuthService from "@/services/auth.service";
import {useAuth0} from "@auth0/auth0-vue";
import * as DateUtils from "@/helpers/dateUtils";


export const useUserStore = defineStore("user", () => {
    const user = ref<IUser | null>(null);
    const sessionRestored = ref(false);
    const saveSuccess = ref(false);
    const saveError = ref(false);

    const i18n = useI18n({ useScope: "global" });
    const tripsStore = useTripsStore();

    const {
        user: auth0User,
        isAuthenticated,
        isLoading,
        logout: auth0Logout,
        getAccessTokenSilently,
    } = useAuth0();

    const isLoggedIn = computed(() => isAuthenticated.value);

    const userSafe = computed(() => user.value || {});

    async function checkProfileComplete(): Promise<boolean> {
        try {
            const profile = await AuthService.getProfile();

            const isComplete =
                !!profile.data.firstName &&
                !!profile.data.lastName &&
                !!profile.data.email

            return isComplete;
        } catch (error) {
            console.error("Failed to check profile completeness:", error);
            return false;
        }
    }


    function logout() {
        user.value = null;
        sessionStorage.clear();
        tripsStore.trips = [];

        auth0Logout({
            logoutParams: {
                returnTo: window.location.origin,
            },
        });
    }


    async function restoreSession() {
        if (isLoading.value) return;

        if (sessionRestored.value) return;

        console.log("Restoring session...");

        try {
            if (isAuthenticated.value && auth0User.value) {
                const token = await getAccessTokenSilently().catch(() => null);

                if (!token) {
                    console.warn("No token available, logging out...");
                    logout();
                    return;
                }

                if (!user.value) {
                    await fetchUserProfile();
                }
            }
        } catch (e: any) {
            console.error("Session restore failed:", e?.message, e?.response?.data || e);
            logout();
        } finally {
            sessionRestored.value = true;
        }
    }


    watch([isAuthenticated, isLoading], async ([auth, loading]) => {
        if (loading || sessionRestored.value || !auth || user.value) return;

        
        // Only restore if we came from a page reload (no auth0User yet)
        if (!auth0User.value) return;

        await restoreSession();
    },
        {immediate: true}
    );

    async function changePassword() {
        if (!user.value?.email) {
            throw new Error('User email is not available');
        }

        return await AuthService.requestPasswordResetEmail(user.value.email);
    }

    // Profile & Image Operations
    async function fetchUserProfile() {
        try {
            const profile = await AuthService.getProfile();
            console.log("Profile data: fetch user profile", profile.data);

            user.value = {
                // ...auth0User.value,
                ...profile.data,
                birthDate: DateUtils.fromApiDate(profile.data.birthDate, i18n.locale.value),
                passportExpiryDate: DateUtils.fromApiDate(profile.data.passportExpiryDate, i18n.locale.value),
                nationality: profile.data.nationality?.id,
            };
        } catch (error) {
            console.error("Failed to fetch user profile:", error);
            throw error;
        }
    }

    async function updateUserProfile(payload: Partial<IUser>) {
        try {
            if (payload.birthDate) {

                payload.birthDate = DateUtils.toApiDate(payload.birthDate, i18n.locale.value);
            }
            if (payload.passportExpiryDate) {
                payload.passportExpiryDate = DateUtils.toApiDate(payload.passportExpiryDate, i18n.locale.value);
            }

            const response = await AuthService.updateProfile(payload);
            user.value = {
                ...user.value,
                ...response.data,
            };
        } catch (error) {
            console.error("Profile update failed:", error);
            throw error;
        }
    }

    async function fetchProfileImage() {
        try {
            const response = await AuthService.getProfileImage();
            if (user.value) {
                user.value.picture = response.data.picture;
            }
        } catch (error) {
            console.error("Failed to fetch profile image:", error);
            throw error;
        }
    }

    async function uploadProfileImage(file: File) {
        const base64Image = await fileToBase64(file);
        try {
            const response = await AuthService.uploadProfileImage({
                image: base64Image,
                filename: file.name,
            });
            if (user.value) {
                user.value.picture = response.data.picture;
            }
        } catch (error) {
            console.error("Image upload failed:", error);
            throw error;
        }
    }

    async function handleSaveProfile(formUser: IUser) {
        saveSuccess.value = false;
        saveError.value = false;
        try {
            await updateUserProfile({ ...formUser });
            await fetchUserProfile();
            saveSuccess.value = true;
        } catch (error) {
            console.error("Failed to save profile and refresh data:", error);
            saveError.value = true;
        }
    }

    function fileToBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () =>
                resolve(reader.result?.toString().split(",")[1] || "");
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    }


    async function uploadPassportDocument(file: File) {
        try {
            const base64Data = await fileToBase64(file);

            const response = await AuthService.uploadPassportImages({
                file: base64Data,
                filename: file.name,
                type: file.type,
                size: file.size,
            });
            
            
            let serverUuid = '';
            if (typeof response.data === 'string') {
                serverUuid = response.data.replace(/[\[\]]/g, '');
            } else if (Array.isArray(response.data)) {
                serverUuid = response.data[0];
            } else if (response.data.uuid) {
                serverUuid = response.data.uuid;
            } else {
                throw new Error('Cannot extract UUID from upload response');
            }

            if (user.value) {
                if (!Array.isArray(user.value.passportDocuments)) {
                    user.value.passportDocuments = [];
                }

                user.value.passportDocuments.push({
                    uuid: serverUuid,
                    fileName: file.name,
                    fileType: file.type || null,
                    size: file.size,
                    fileUrl: '' 
                });
            }

            return serverUuid;

        } catch (error) {
            console.error("Passport upload failed:", error);
            throw error;
        }
    }

    async function getPassportDocuments() {
        try {
            const response = await AuthService.getPassportImages();
            if (user.value) {
                user.value.passportDocuments = response.data;
            }
        } catch (error) {
            console.error("Fetching passport documents failed:", error);
            throw error;
        }
    }

    async function deletePassportDocument(uuid: string) {
        try {
            await AuthService.deletePassportImages(uuid);
            if (user.value) {
                user.value.passportDocuments = user.value.passportDocuments.filter(doc => doc.uuid !== uuid);
            }
        } catch (error) {
            console.error("Passport document deletion failed:", error);
            throw error;
        }
    }

    async function uploadLicenseDocument(file: File) {
        try {
            const base64Data = await fileToBase64(file);

            const response = await AuthService.uploadLicenseImages({
                file: base64Data,
                filename: file.name,
                type: file.type,
                size: file.size,
            },);

            let serverUuid = '';
            if (typeof response.data === 'string') {
                serverUuid = response.data.replace(/[\[\]]/g, '');
            } else if (Array.isArray(response.data)) {
                serverUuid = response.data[0];
            } else if (response.data.uuid) {
                serverUuid = response.data.uuid;
            } else {
                throw new Error('Cannot extract UUID from upload response');
            }
            
            if (user.value) {
                if (!Array.isArray(user.value.licenseDocuments)) {
                    user.value.licenseDocuments = [];
                }

                user.value.licenseDocuments.push({
                    uuid: serverUuid,
                    fileName: file.name,
                    fileType: file.type || null,
                    size: file.size,
                    fileUrl: ''
                });
            }

            return serverUuid;
            
        } catch (error) {
            console.error("License upload failed:", error);
            throw error;
        }
    }

    async function uploadVHFLicenseDocument(file: File) {
        try {
            const base64Data = await fileToBase64(file);

            const response = await AuthService.uploadVHFLicenseImages({
                file: base64Data,
                filename: file.name,
                type: file.type,
                size: file.size,
            });

            let serverUuid = '';
            if (typeof response.data === 'string') {
                serverUuid = response.data.replace(/[\[\]]/g, '');
            } else if (Array.isArray(response.data)) {
                serverUuid = response.data[0];
            } else if (response.data.uuid) {
                serverUuid = response.data.uuid;
            } else {
                throw new Error('Cannot extract UUID from upload response');
            }

            if (user.value) {
                if (!Array.isArray(user.value.vhfLicenseDocuments)) {
                    user.value.vhfLicenseDocuments = [];
                }

                user.value.vhfLicenseDocuments.push({
                    uuid: serverUuid,
                    fileName: file.name,
                    fileType: file.type || null,
                    size: file.size,
                    fileUrl: ''
                });
            }

            return serverUuid; // the server UUID as string
            
        } catch (error) {
            console.error("VHF License upload failed:", error);
            throw error;
        }
    }

    async function getVHFLicenseDocuments() {
        try {
            const response = await AuthService.getVHFLicenseImages();
            if (user.value) {
                user.value.vhfLicenseDocuments = response.data;
            }
        } catch (error) {
            console.error("Fetching VHF license documents failed:", error);
            throw error;
        }
    }

    async function deleteVHFLicenseDocument(uuid: string) {
        try {
            const response = await AuthService.deleteVHFLicenseImage(uuid);
            if (user.value) {
                user.value.vhfLicenseDocuments = user.value.vhfLicenseDocuments.filter(doc => doc.uuid !== uuid);
            }
        } catch (error) {
            console.error("VHF license document deletion failed:", error);
            throw error;
        }
    }

    async function getLicenseDocuments() {
        try {
            const response = await AuthService.getLicenseImages();
            if (user.value) {
                user.value.licenseDocuments = response.data;
            }
        } catch (error) {
            console.error("Fetching license documents failed:", error);
            throw error;
        }
    }

    async function deleteLicenseDocument(uuid: string) {
        try {
            const response = await AuthService.deleteLicenseImage(uuid);
            if (user.value) {
                user.value.licenseDocuments = user.value.licenseDocuments.filter(doc => doc.uuid !== uuid);
            }
        } catch (error) {
            console.error("License document deletion failed:", error);
            throw error;
        }
    }

    async function deactivateUserAccount() {
        try {
            await AuthService.deactivateAccount();
            logout();
        } catch (error) {
            console.error("Account deactivation failed:", error);
            throw error;
        }
    }

    return {
        user,
        isLoggedIn,
        isLoading,
        sessionRestored,
        checkProfileComplete,
        userSafe,
        saveSuccess,
        saveError,
        changePassword,
        logout,
        restoreSession,
        fetchUserProfile,
        updateUserProfile,
        fetchProfileImage,
        uploadProfileImage,
        handleSaveProfile,
        uploadPassportDocument,
        getPassportDocuments,
        deletePassportDocument,
        uploadLicenseDocument,
        getLicenseDocuments,
        deleteLicenseDocument,
        uploadVHFLicenseDocument,
        getVHFLicenseDocuments,
        deleteVHFLicenseDocument,
        deactivateUserAccount
    };
});
